import { PublicClientApplication } from '@azure/msal-browser';
import api from './api';

const SCOPES = ['openid', 'profile', 'email', 'User.Read'];

let msalInstance = null;
let initPromise = null;
let ssoConfigCache = null;

// Récupère la config publique du SSO depuis le backend (clientId, tenantId...).
// Rien de secret n'est jamais exposé ici (pas de client secret).
export async function getSsoConfig() {
  if (ssoConfigCache) return ssoConfigCache;
  const { data } = await api.get('/auth/sso/config');
  ssoConfigCache = data.data;
  return ssoConfigCache;
}

async function getMsalInstance() {
  if (msalInstance) { await initPromise; return msalInstance; }
  const config = await getSsoConfig();
  if (!config.enabled) throw new Error('SSO désactivé');

  const authority = config.authorityType === 'single'
    ? `https://login.microsoftonline.com/${config.tenantId}`
    : 'https://login.microsoftonline.com/common';

  msalInstance = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority,
      redirectUri: config.redirectUri
    },
    cache: { cacheLocation: 'sessionStorage' }
  });
  initPromise = msalInstance.initialize();
  await initPromise;
  return msalInstance;
}

// Vide le cache MSAL local. Sans cela l'instance garde le compte en session :
// la connexion suivante réutilise silencieusement la même identité, sans même
// repasser par Microsoft — d'où l'impression que la déconnexion n'a rien fait.
//
// On ne déclenche volontairement PAS de logoutRedirect : il déconnecterait la
// personne de Microsoft dans tout le navigateur, donc d'Outlook, de Teams et du
// reste. Oublier le compte côté portail suffit.
export async function clearMsalCache() {
  try {
    if (msalInstance) {
      await initPromise;
      await msalInstance.clearCache();
    }
  } catch {
    // Un cache récalcitrant ne doit pas empêcher la déconnexion.
  }
  try {
    // Object.keys rend un instantané : retirer pendant la boucle est sans risque.
    for (const cle of Object.keys(sessionStorage)) {
      if (cle.startsWith('msal.')) sessionStorage.removeItem(cle);
    }
  } catch {
    // Navigation privée ou stockage refusé.
  }
  msalInstance = null;
  initPromise = null;
  ssoConfigCache = null;
}

// Flow redirect plutôt que popup : login.microsoftonline.com envoie lui-même un
// Cross-Origin-Opener-Policy strict sur ses pages, ce qui casse systématiquement
// la référence window.opener attendue par loginPopup ("User cancelled the flow"),
// quelle que soit notre propre config COOP. Le redirect évite le problème : pas
// de popup, toute la page navigue vers Microsoft puis revient.
export async function startMicrosoftLogin() {
  const instance = await getMsalInstance();
  await instance.loginRedirect({ scopes: SCOPES });
}

// À appeler au chargement de la page de login : si on revient d'un redirect
// Azure, récupère la réponse et l'échange contre un JWT interne au Portail.
// Retourne null si on n'est pas dans ce cas (chargement normal de la page).
export async function completeMicrosoftLoginIfReturning() {
  const instance = await getMsalInstance();
  const result = await instance.handleRedirectPromise();
  if (!result) return null;
  const { data } = await api.post('/auth/sso', { idToken: result.idToken });
  return data.data;
}
